'use client'

import { Price } from '@/components/Price'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useEnhancedCart } from '@/providers/EnhancedCart'
import { ShoppingCart, Tag } from 'lucide-react'
import Image from 'next/image'
import { Link, usePathname } from '@/i18n/routing'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { motion } from 'framer-motion'

import { DeleteItemButton } from './DeleteItemButton'
import { EditItemQuantityButton } from './EditItemQuantityButton'
import { CartItemQuantityInput } from './CartItemQuantityInput'
import { OpenCartButton } from './OpenCart'
import { Button } from '@/components/ui/button'
import { Product } from '@/payload-types'
import { AppliedDiscounts } from './AppliedDiscounts'
import { ShippingCalculator } from '@/components/checkout/ShippingCalculator'

export function CartModal() {
  const t = useTranslations('cart')
  const tProduct = useTranslations('product')
  const {
    cart,
    cartSubtotal,
    cartTotal,
    bulkVolumeDiscount,
    discountBreakdown,
    shippingInfo,
    manualCouponDiscount,
    containsManualDeliveryItems,
  } = useEnhancedCart()
  const [isOpen, setIsOpen] = useState(false)
  const [fetchedProducts, setFetchedProducts] = useState<Record<string | number, Product>>({})

  const pathname = usePathname()

  useEffect(() => {
    // Close the cart modal when the pathname changes.
    setIsOpen(false)
  }, [pathname])

  // Listen for custom open-cart event
  useEffect(() => {
    const handleOpenCart = () => setIsOpen(true)
    window.addEventListener('open-cart', handleOpenCart)
    return () => window.removeEventListener('open-cart', handleOpenCart)
  }, [])

  // Workaround: Fetch full product data where it is missing in the cart data
  useEffect(() => {
    if (!cart?.items) return

    const fetchMissingData = async () => {
      const missingIds: (string | number)[] = []

      cart.items?.forEach((item: any) => {
        const product = item.product
        if (typeof product === 'object' && product && product.id) {
          const hasImage = product.meta?.image || (product.gallery && product.gallery.length > 0)
          const hasPrice = product.basePrice !== undefined || product.price !== undefined

          if ((!hasImage || !hasPrice) && fetchedProducts[product.id] === undefined) {
            missingIds.push(product.id)
          }
        }
      })

      if (missingIds.length === 0) return

      try {
        const query = new URLSearchParams()
        query.set('limit', String(missingIds.length))
        query.set('depth', '2')
        missingIds.forEach((id) => query.append('where[id][in]', String(id)))

        const res = await fetch(`/api/products?${query.toString()}`)
        if (res.ok) {
          const json = await res.json()
          const newProducts: Record<string | number, Product> = {}
          json.docs.forEach((p: Product) => {
            newProducts[p.id] = p
          })
          setFetchedProducts((prev) => ({ ...prev, ...newProducts }))
        }
      } catch (e) {
        console.error('Failed to fetch product data batch', e)
      }
    }

    fetchMissingData()
  }, [cart, fetchedProducts])

  const totalQuantity = useMemo(() => {
    if (!cart || !cart.items || !cart.items.length) return undefined
    return (cart.items as any[]).reduce(
      (quantity: number, item: any) => (item.quantity || 0) + quantity,
      0,
    )
  }, [cart])

  const calculatedTotal = useMemo(() => {
    if (!cart?.items) return 0

    const total = (cart.items as any[]).reduce((acc: number, item: any) => {
      let product = item.product
      if (typeof product !== 'object' || !product) return acc

      // Use fetched product if available
      if (fetchedProducts[product.id]) {
        product = fetchedProducts[product.id]
      }

      let price = product.basePrice || product.price

      const variant = item.variant
      if (item.selectedVariantData && item.selectedVariantData.priceTotal !== undefined) {
        price = item.selectedVariantData.priceTotal
      } else if (Boolean(variant) && typeof variant === 'object') {
        price = (variant as any)?.priceInEUR || (variant as any)?.price || price
      }

      const quantity = item.quantity || 1
      const lineTotal = (Number(price) || 0) * quantity
      return acc + lineTotal
    }, 0)

    return total
  }, [cart, fetchedProducts])

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger asChild>
        <OpenCartButton quantity={totalQuantity} />
      </SheetTrigger>

      <SheetContent className="flex flex-col w-full sm:max-w-md border-l border-neutral-200 dark:border-neutral-800">
        <SheetHeader className="px-6 py-6 border-b border-neutral-100 dark:border-neutral-900">
          <SheetTitle className="font-mono text-xl uppercase tracking-widest">
            {t('title')}
          </SheetTitle>
          <SheetDescription className="font-mono text-xs text-neutral-800 dark:text-neutral-200 uppercase tracking-wider">
            {t('summary')}
          </SheetDescription>
        </SheetHeader>

        {!cart || cart?.items?.length === 0 ? (
          <div className="grow flex flex-col items-center justify-center gap-6 p-8">
            <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
              <ShoppingCart className="h-12 w-12 text-neutral-400" strokeWidth={1} />
            </div>
            <div className="text-center space-y-4">
              <p className="text-center text-sm font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
                {t('empty')}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="font-mono text-xs uppercase tracking-widest"
                onClick={() => setIsOpen(false)}
              >
                {t('continueShopping') || 'Continue Shopping'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grow flex flex-col overflow-hidden">
            <ul className="grow overflow-y-auto px-6 py-2 custom-scrollbar">
              {cart?.items?.map((item: any, i: number) => {
                let product = item.product

                if (typeof product !== 'object' || !item || !product || !product.slug)
                  return <React.Fragment key={i} />

                // Use fetched product if available
                if (fetchedProducts[product.id]) {
                  product = fetchedProducts[product.id]
                }

                const variant = item.variant
                const isVariant = Boolean(variant) && typeof variant === 'object'

                // Image logic: Check product meta, then first gallery image
                const metaImage =
                  product.meta?.image && typeof product.meta?.image === 'object'
                    ? product.meta.image
                    : undefined

                const firstGalleryImage =
                  product.gallery &&
                    Array.isArray(product.gallery) &&
                    product.gallery.length > 0 &&
                    typeof product.gallery[0]?.image === 'object'
                    ? product.gallery[0].image
                    : undefined

                const image = metaImage || firstGalleryImage

                // Get price, checking variant first, then product basePrice, then fallback
                let price = product.basePrice || product.price
                const quantity = item.quantity || 1

                if (item.selectedVariantData && item.selectedVariantData.priceTotal !== undefined) {
                  price = item.selectedVariantData.priceTotal
                } else if (isVariant) {
                  price = (variant as any)?.priceInEUR || (variant as any)?.price || price
                }

                return (
                  <li
                    className="group relative flex items-center gap-4 py-6 border-b border-neutral-100 dark:border-neutral-900 last:border-0"
                    key={i}
                  >
                    <div className="absolute top-4 right-0 z-40 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                      <DeleteItemButton item={item} />
                    </div>
                    <div className="flex w-full flex-row gap-4">
                      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-sm border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
                        <Link
                          className="relative block h-full w-full"
                          href={`/products/${product.slug}`}
                        >
                          {image?.url && (
                            <Image
                              alt={image?.alt || product?.title || ''}
                              className="object-cover transition-transform duration-500 group-hover:scale-110"
                              fill
                              src={
                                image.url.startsWith('http')
                                  ? image.url
                                  : `${process.env.NEXT_PUBLIC_SERVER_URL || 'http://localhost:3000'}${image.url.startsWith('/') ? '' : '/'}${image.url}`
                              }
                              sizes="96px"
                            />
                          )}
                        </Link>
                      </div>

                      <div className="flex flex-1 flex-col justify-between py-1">
                        <div className="pr-6">
                          <Link
                            href={`/products/${product.slug}`}
                            className="font-mono text-sm uppercase tracking-tight hover:text-[var(--neon-orange)] transition-colors block"
                          >
                            {product?.title}
                          </Link>
                          {(isVariant && variant) || item.selectedVariantData ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {item.selectedVariantData
                                ? item.selectedVariantData.options?.map((option: any, idx: number) => (
                                    <span
                                      key={idx}
                                      className="inline-block px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 font-mono text-[9px] text-neutral-600 dark:text-neutral-400 uppercase tracking-widest rounded-sm"
                                    >
                                      {option.value}
                                    </span>
                                  ))
                                : ((variant as any).options as any[])?.map((option, idx) => {
                                    if (typeof option === 'object') {
                                      return (
                                        <span
                                          key={idx}
                                          className="inline-block px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 font-mono text-[9px] text-neutral-600 dark:text-neutral-400 uppercase tracking-widest rounded-sm"
                                        >
                                          {option.label}
                                        </span>
                                      )
                                    }
                                    return null
                                  }).filter(Boolean) || (
                                    <span className="inline-block px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 font-mono text-[9px] text-neutral-600 dark:text-neutral-400 uppercase tracking-widest rounded-sm">
                                      {(variant as any).title}
                                    </span>
                                  )}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex items-end justify-between">
                          <div className="flex items-center rounded-sm border border-neutral-200 dark:border-neutral-800">
                            <EditItemQuantityButton item={item} type="minus" />
                            <CartItemQuantityInput item={item} />
                            <EditItemQuantityButton item={item} type="plus" />
                          </div>

                          {(typeof price === 'number' && (
                            <div className="flex flex-col items-end">
                              <Price
                                amount={price * quantity}
                                className="font-mono text-sm font-bold text-[var(--neon-orange)]"
                              />
                              <span className="text-[9px] font-mono text-neutral-700 dark:text-neutral-200">
                                {tProduct('vatIncluded', { rate: product.customTaxRate ?? 20 })}
                              </span>
                            </div>
                          )) || (
                              <div className="text-[10px] font-mono uppercase text-muted-foreground">
                                {`Price N/A (id: ${product.id})`}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>

            <div className="p-6 bg-neutral-50 dark:bg-neutral-950 border-t border-neutral-100 dark:border-neutral-900">
              {/* Shipping Calculator - shows progress to free shipping */}
              <ShippingCalculator
                shippingInfo={shippingInfo}
                cartTotal={Math.max(0, cartSubtotal - manualCouponDiscount - bulkVolumeDiscount)}
                className="mb-4"
                showProgressBar={true}
                containsManualDeliveryItems={containsManualDeliveryItems}
              />

              {/* Show discount breakdown if discounts are applied */}
              <AppliedDiscounts
                discountBreakdown={discountBreakdown}
                bulkVolumeDiscount={bulkVolumeDiscount}
                className="mb-4 bg-card/50"
              />

              <div className="mb-6 flex items-center justify-between">
                <p className="font-mono text-xs uppercase tracking-widest text-neutral-800 dark:text-neutral-200">
                  {t('grandTotal')}
                </p>
                {/* Use enhanced cart total which includes discounts */}
                <Price
                  amount={(cartTotal?.raw ?? 0) / 100}
                  className="font-mono text-xl font-bold text-[var(--neon-orange)]"
                />
              </div>

              <Link
                className="block w-full group/checkout"
                href="/checkout"
                onClick={() => setIsOpen(false)}
              >
                <Button
                  variant="neon-white"
                  className="w-full flex items-center justify-center gap-3 rounded-lg dark:bg-black dark:text-[var(--neon-orange)] dark:border-white/10"
                >
                  <span className="relative z-10">{t('checkout')}</span>
                  <span className="relative z-10 opacity-70 group-hover/checkout:translate-x-1.5 transition-transform duration-300">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
